import { createContext, type ReactNode, useContext, useMemo } from "react";
import { ToastStack } from "./ToastStack";
import type { ToastOptions } from "./toastModel";
import { useToastQueue } from "./useToastQueue";

export type { ToastOptions, ToastTone } from "./toastModel";

interface ToastContextValue {
  show: (options: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const { toasts, show, dismiss } = useToastQueue();
  const value = useMemo(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a ToastProvider");
  return ctx;
}
